'use client';
import { useEffect, useState } from 'react';
import { DialogDescription, DialogTrigger } from '@radix-ui/react-dialog';
import MasonEasyPayCreditIcon from '@/components/customercenter-ui/svgs/masoneasypay-credit';
import MasseysCreditIcon from '@/components/customercenter-ui/svgs/masseys-credit';
import StoneberryCreditIcon from '@/components/customercenter-ui/svgs/stoneBerryCredit';
import { useAccount } from '@/frontastic';
import { useFormat } from '@/frontastic/hooks/useFormat';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { formatCurrency, getAgentName, getSelectedDivision } from '@/helpers/utils/siteUtils';
import { PhoneRecordingPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import DownPaymentModal from './downPayment';
import { transformCustomerDetailsData } from '../../customer-detail/utils';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';

interface DownPaymentRequestProps {
  open: any;
  setOpen: any;
  order: any;
  handleDownpaymentSuccess: any;
  customerDetails: any;
  loading: any;
  isOrderDetails?: boolean;
  isSoldAccount: boolean;
}
const DownPaymentRequest: React.FC<DownPaymentRequestProps> = ({
  open,
  setOpen,
  order,
  handleDownpaymentSuccess,
  customerDetails,
  loading,
  isOrderDetails = false,
  isSoldAccount,
}) => {
  const { stopPhoneRecording } = useAccount();
  const { formatMessage: formatDownPaymentRequest } = useFormat({ name: 'downpayment-request' });
  const [downPaymentOpen, setDownPaymentOpen] = useState(false);

  const downPaymentAmount = isOrderDetails
    ? isSoldAccount
      ? order?.orderSummaryInfo?.total
      : order?.PostSubmissionInfo?.downPaymentAmountRequested
    : order?.soaOrderDetails?.CreditStatus?.DownPaymentInfo?.downPaymentAmount ?? '0.0';

  const divisionCode = isOrderDetails ? order?.OrderInfo?.divisionCode ?? '' : getSelectedDivision();
  const { billingAddress, creditAccountId, primaryEmail, omsCustomerId, creditAccount } = transformCustomerDetailsData({
    customerDetails,
  } as any);

  useEffect(() => {
    const handleStopRecording = async () => {
      if (downPaymentOpen) {
        const payload = {
          data: {
            agentEmailAddress: getAgentName(),
            divisionCode: getSelectedDivision(),
          },
        } as PhoneRecordingPayload;

        await stopPhoneRecording(payload);
      }
    };
    handleStopRecording();
  }, [downPaymentOpen]);

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {isOrderDetails && (
          <DialogTrigger className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium">
            Make Down Payment
          </DialogTrigger>
        )}
        <DialogContent className="h-auto max-h-[90vh] max-w-[640px] overflow-scroll bg-white sm:rounded-md">
          {loading && <Loader />}
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              <section>
                <div>
                  {divisionCode === 'ZB' ? (
                    <StoneberryCreditIcon />
                  ) : divisionCode === 'ZY' ? (
                    <MasseysCreditIcon />
                  ) : divisionCode === 'ZD' ? (
                    <MasonEasyPayCreditIcon />
                  ) : (
                    <StoneberryCreditIcon />
                  )}
                </div>
                <div className="mt-4 text-2xl font-bold">
                  {formatDownPaymentRequest({
                    id: 'radioGroupDownPaymentRequest',
                    defaultMessage: 'Down Payment Request',
                  })}
                </div>
              </section>
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-base font-medium">
            {formatDownPaymentRequest({
              id: 'radioGroupDescription',
              defaultMessage: 'We can approve your order right now if you make a down payment of: ',
            })}
            <span className="text-xl font-bold">
              {formatDownPaymentRequest({
                id: 'radioGroupAmount',
                defaultMessage: formatCurrency(downPaymentAmount),
              })}
            </span>
          </DialogDescription>
          <section className="mt-4 flex justify-start gap-4">
            <Button className="rounded-md bg-deepBlue p-4 text-white" onClick={() => setDownPaymentOpen(true)}>
              {mapLocaleToMeaningfulFormat().makeDownPayment}
            </Button>
            <Button className="border-[1px] border-silverGray" onClick={() => setOpen(false)}>
              Maybe Later
            </Button>
          </section>
          <section className="my-4">
            <p className="mb-2 text-lg font-semibold">Why am I being asked to make a down payment?</p>
            <p className="text-base font-normal">
              Down payments are occasionally required for final credit approval. There are a number of factors that
              determine if a down payment is needed and what amount is required. Whether you make a down payment or not,
              you will receive a letter in the mail with more information related to your credit request within 7-10
              days.
            </p>
          </section>
          <section className="mb-4">
            <p className="mb-2 text-lg font-semibold">Need to wait?</p>
            <p className="text-base font-normal">
              If you decide not to make a down payment right now, we will hold your order details but can&apos;t
              guarantee your item(s) will remain in stock.
            </p>
          </section>
          <section className="mb-4">
            <p className="mb-2 text-lg font-semibold">To make a down payment later:</p>
            <p className="text-base font-normal">
              If you&apos;d like to make a down payment later, you can give us a call back and we&apos;d be more than
              happy to help. Otherwise, down payments can be made online with a web account or can be sent in through
              the mail.
            </p>
          </section>
          {downPaymentOpen && (
            <DownPaymentModal
              open={downPaymentOpen}
              setOpen={setDownPaymentOpen}
              billingAddress={billingAddress}
              primaryEmail={primaryEmail}
              creditAccountId={creditAccountId}
              creditAccount={creditAccount}
              omsCustomerId={omsCustomerId}
              order={order}
              handleDownpaymentSuccess={handleDownpaymentSuccess}
              isOrderDetails={isOrderDetails}
              isSoldAccount={isSoldAccount}
            ></DownPaymentModal>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DownPaymentRequest;
